import React from 'react';
import { handleActionError } from '../../../utils/engineering-tools';
import { BearingType, BearingTypeCode, GreaseQuantityPayload, TimkenGreaseSpecificationType, TimkenUnit } from '../../../../components/bearing-search/types/types';
import { convertNumberInput, isValidRange } from '../../../utils/validations';
import { bearingTypes } from '../../../../components/bearing-search/api/api';
import { GreaseQuantityPartPayload } from '../InitialGreaseFill';

type FillBySpecificationValidationMap = Map<keyof FillBySpecificationState, boolean>;

export type FillBySpecificationState = {
	unit: TimkenUnit;
	specsType: TimkenGreaseSpecificationType;
	partType: string;
	bearingType: string;
	bearingSubType?: string;
	innerDiameter?: number;
	outerDiameter?: number;
	bearingWidth?: number;
	bearingWeight?: number;
	speedDataID?: number;
	percentageFillData?: number;
	requestData?: GreaseQuantityPartPayload;
	validation: FillBySpecificationValidationMap;
};

const validationFields: Array<keyof FillBySpecificationState> = [
	'innerDiameter',
	'outerDiameter',
	'bearingWidth',
	'bearingWeight',
	'percentageFillData',
];

export const initialFillBySpecsState: FillBySpecificationState = {
	unit: 'M',
	specsType: 'Timken',
	partType: bearingTypes?.[ 0 ]?.partType,
	bearingType: bearingTypes?.[ 0 ]?.bearingType,
	validation: new Map( validationFields.map( ( key ) => [ key, false ] ) ),
};

export type FillBySpecificationAction =
	| { type: 'change_unit'; payload: TimkenUnit }
	| { type: 'change_specs_type'; payload: TimkenGreaseSpecificationType }
	| { type: 'change_bearing_type'; payload: BearingType }
	| { type: 'change_number_field'; payload: [keyof FillBySpecificationState, unknown] }
	| { type: 'update_request_data' }
	| { type: 'reset'; payload: FillBySpecificationState };

const getFillBySpecsFormData = ( state: FillBySpecificationState ): [FillBySpecificationValidationMap, Omit<GreaseQuantityPayload, 'isDBRetrieval' | 'isReportRequired'> | undefined] => {
	const {
		unit,
		bearingType,
		bearingSubType,
		innerDiameter,
		outerDiameter,
		bearingWidth,
		bearingWeight,
		speedDataID,
		percentageFillData,
		specsType,
	} = state;

	const validation = new Map( state.validation );

	validation.set( 'bearingWidth', isValidRange( bearingWidth, 0, Infinity, true ) );

	validation.set( 'bearingWeight', isValidRange( bearingWeight, 0, Infinity, true ) );

	validation.set( 'outerDiameter', isValidRange( outerDiameter, 0, Infinity, true ) );

	validation.set( 'innerDiameter', isValidRange( innerDiameter, 0, Infinity, true ) );

	if ( specsType === 'Timken' ) {
		validation.set( 'percentageFillData', true );
		validation.set( 'speedDataID', isValidRange( speedDataID, 0, 1000000, true ) );
	} else {
		validation.set( 'speedDataID', true );
		validation.set( 'percentageFillData', isValidRange( percentageFillData, 0, 100, true ) );
	}

	const isFormValid = ! Array.from( validation.values() ).some( ( valid ) => ! valid );
	let requestData: Omit<GreaseQuantityPayload, 'isDBRetrieval' | 'isReportRequired'> | undefined;

	if ( isFormValid ) {
		requestData = {
			unit,
			bearingType,
			bearingSubType,
			innerDiameter,
			outerDiameter,
			bearingWeight,
			bearingWidth,
		};

		if ( specsType === 'Timken' ) {
			requestData.speedDataID = speedDataID;
		} else {
			requestData.percentageFillData = percentageFillData;
		}
	}

	return [ validation, requestData ];
};

const greaseFillBySpecsReducer: React.Reducer<
	FillBySpecificationState,
	FillBySpecificationAction
> = ( state, action ) => {
	switch ( action.type ) {
		case 'change_unit': {
			return {
				...state,
				unit: action.payload,
			};
		}
		case 'change_specs_type': {
			return {
				...state,
				specsType: action.payload,
			};
		}
		case 'change_bearing_type': {
			const { bearingType, partType, bearingSubType } = action.payload;

			return {
				...state,
				bearingType,
				partType,
				bearingSubType: bearingType === BearingTypeCode.TRB ? bearingSubType : undefined,
			};
		}
		case 'change_number_field': {
			const [ key, value ] = action.payload;

			return {
				...state,
				[ key ]: convertNumberInput( value ),
			};
		}
		case 'update_request_data': {
			const [ validation, requestData ] = getFillBySpecsFormData( state );

			return {
				...state,
				validation,
				requestData,
			};
		}
		case 'reset': {
			return {
				...action.payload,
			};
		}
	}

	return handleActionError( action );
};

export default greaseFillBySpecsReducer;
